/* WHO COMPETES UNDER THIS BODY — one answer, whatever the level.
 *
 * Brian: "It has stale information in the number of entities for everything."
 *
 * ⚠️ THE DEFECT THIS FIXES. The Governing Bodies page asked ONE module for its
 * member list — `sportAffiliation.js`, which is the COLLEGE roster (875 schools
 * across conferences). So every HIGH-SCHOOL card called `schoolsIn('NCHSAA')`,
 * got nothing back, and rendered:
 *
 *     "Teams not sourced yet — a champion here cannot be matched to a school."
 *
 * …while 22,894 harvested high-school members sat in `hsRosters.js` unused. The
 * page was not showing a stale NUMBER so much as answering a question with the
 * wrong dataset entirely, and the honest-blank copy made that read as a
 * sourcing gap rather than a wiring bug. A page that says "not sourced" about
 * data we hold is worse than one that says nothing: it sends somebody to go
 * find what is already on disk.
 *
 * So the level picks the source, and there is exactly one place that decides:
 *
 *     hs      → hsRosters.js       126 bodies, the associations' own directories
 *     college → sportAffiliation.js  conference rosters + per-sport departures
 *     canada  → sportAffiliation.js  same shape as college
 *     pro     → neither — a league's "members" are franchises, not schools
 *
 * ⚠️ `complete` TRAVELS WITH THE ANSWER. "9 teams compete here" and "9 known —
 * full roster not sourced yet" are different claims, and the card must not be
 * free to guess which one it is making. A count with no completeness flag is
 * how a partial roster starts reading as a full one.
 *
 * Pure. No React, no fetch — so verify-bodies.mjs drives the real thing.
 */
import { HS_ROSTERS } from './hsRosters.js'
import { schoolsIn, isRosterComplete } from './sportAffiliation.js'
import { COLLEGE_ROSTERS, PRO_ROSTERS } from './collegeRosters.js'

const L = (v) => String(v ?? '').trim().toLowerCase()

/* ⚠️ THE REGISTRY AND THE HARVEST DISAGREE ON PUNCTUATION, and it silently
 * costs a whole body. The registry says "U SPORTS" with a space; the harvest
 * file is USPORTS.json. That mismatch resolved to nothing and Canada's largest
 * body — 58 universities — rendered as unsourced. Same class of failure as the
 * TAPPS "-Dallas" suffix: the data was there, the key just did not survive. */
const rosterKeyFor = (v) => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
const lookup = (map, acronym) => map[acronym]
  || map[String(acronym).toUpperCase()]
  || map[Object.keys(map).find((k) => rosterKeyFor(k) === rosterKeyFor(acronym))]

/** Every HS body keyed by acronym → its roster entry, with the state alongside. */
const HS_BY_ACRONYM = new Map()
for (const [st, entry] of Object.entries(HS_ROSTERS || {})) {
  for (const b of (entry.bodies || [])) {
    /* ⚠️ KEYED BY ACRONYM + STATE, NOT ACRONYM ALONE. Real collisions exist and
     * they are not exotic: Wisconsin's WIAA and Washington's WIAA are different
     * associations, Indiana and Iowa both run an "IHSAA", and New York's CHSAA
     * is not Colorado's. Keying on the acronym alone would hand one state's
     * roster to another state's card — a wrong answer that looks entirely
     * plausible on screen. */
    HS_BY_ACRONYM.set(`${L(b.acronym)}|${L(st)}`, { ...b, st, state: entry.state })
  }
}

/**
 * Members of a governing body.
 *
 * @param entity  an entry from allEntities() — needs { acronym, st }
 * @param level   'hs' | 'college' | 'canada' | 'pro'
 * @returns { members: [{name, only}], complete, source, directoryUrl, sourceKind }
 */
export function membersFor(entity, level = 'hs') {
  const acronym = entity?.acronym
  if (!acronym) return { members: [], complete: false, source: 'none' }

  if (level === 'pro') {
    /* ⚠️ THIS USED TO RETURN NOTHING, AND THAT WAS MY ERROR. The reasoning was
     * that a league's members are FRANCHISES rather than schools, so the level
     * had no roster to show — technically true and practically backwards. SR
     * sells to pro teams; a pro championship is the LARGEST single ring order
     * the business handles, one team and a hundred-odd rings; the Carolina
     * Hurricanes are a live account. Deciding the level had no members meant
     * the highest-value customers on the board rendered blank.
     *
     * A franchise IS a city, so city and state ride along here in a way they
     * do not for a conference. */
    const league = lookup(PRO_ROSTERS, acronym)
    if (league && (league.members || []).length) {
      return {
        members: league.members.map((m) => ({ name: m.n, city: m.c || '', state: m.s || '', conference: m.cf || '', division: m.d || '' })),
        complete: league.complete === true,
        source: 'pro-rosters',
        sourceKind: league.sourceKind,
        directoryUrl: league.directoryUrl,
        sportScope: league.sportScope,
        statedMembers: league.statedMembers,
        note: league.note,
      }
    }
    /* ⚠️ ONLY THE UMBRELLA HEADING IS "not applicable". "Professional leagues"
     * is a section title, not a league, so it has no franchises of its own and
     * stays silent. A REAL league with no roster yet — MiLB today — must report
     * `none`, because calling it n/a would hide a genuine gap behind a label
     * that means "nothing to find here". That is how the whole pro level came
     * to read as empty in the first place. */
    return { members: [], complete: false, source: L(acronym) === 'pro' ? 'n/a' : 'none' }
  }

  if (level === 'hs') {
    const hit = HS_BY_ACRONYM.get(`${L(acronym)}|${L(entity.st)}`)
      || HS_BY_ACRONYM.get(`${L(acronym)}|${L(entity.state)}`)
    if (!hit) return { members: [], complete: false, source: 'none' }
    return {
      members: (hit.schools || []).map((s) => ({ name: typeof s === 'string' ? s : s?.name })).filter((s) => s.name),
      complete: hit.complete === true,
      source: 'hs-rosters',
      sourceKind: hit.sourceKind,
      directoryUrl: hit.directoryUrl,
      note: hit.note,
    }
  }

  /* ⚠️ THE HARVESTED COLLEGE ROSTERS ARE ASKED FIRST, and fall back to
   * sportAffiliation rather than replacing it.
   *
   * collegeRosters.js is the new evidence layer: each body sourced from its own
   * member directory, carrying `statedMembers` so completeness is checked
   * against what the body publishes about itself rather than asserted, plus a
   * `sportScope`. sportAffiliation is the older layer and still holds
   * conferences the harvest has not reached, so replacing it outright would
   * DELETE working rosters to install new ones — a regression dressed as an
   * upgrade. Falling through keeps every conference that worked yesterday.
   *
   * ⚠️ sportScope RIDES ALONG because it is load-bearing. A school in Hockey
   * East is there FOR HOCKEY and plays everything else somewhere else; a caller
   * that treats that membership as general would manufacture a phantom
   * championship for a rep to chase. */
  const harvested = lookup(COLLEGE_ROSTERS, acronym)
  if (harvested && (harvested.members || []).length) {
    return {
      members: harvested.members.map((m) => ({ name: m.n, athleticName: m.a || '', state: m.s || '', division: m.d || '' })),
      complete: harvested.complete === true,
      source: 'college-rosters',
      sourceKind: harvested.sourceKind,
      directoryUrl: harvested.directoryUrl,
      sportScope: harvested.sportScope,
      statedMembers: harvested.statedMembers,
      note: harvested.note,
    }
  }

  /* college + canada — the older conference rosters.
   *
   * ⚠️ TRIED BY NAME **FIRST**, THEN ACRONYM, because sportAffiliation is keyed
   * on the conference's FULL NAME ("Big South Conference") — which is what the
   * page was already passing. Looking up by acronym alone would have returned
   * nothing for every college body and silently converted a working board into
   * "teams not sourced yet", i.e. exactly the bug being fixed here, moved from
   * high school to college. */
  const byName = schoolsIn(entity.name) || []
  const members = byName.length ? byName : (schoolsIn(acronym) || [])
  const key = byName.length ? entity.name : acronym
  return {
    members,
    complete: isRosterComplete(key) === true,
    source: 'sport-affiliation',
  }
}

/** Roll-up across a whole level, for the page's topline. Derived, never typed. */
export function memberTotals(entities = [], level = 'hs') {
  let members = 0, sourced = 0, complete = 0
  for (const e of entities) {
    const r = membersFor(e, level)
    if (r.source === 'n/a') continue
    if (r.members.length) { sourced += 1; members += r.members.length }
    if (r.complete) complete += 1
  }
  return { members, sourced, complete, bodies: entities.filter((e) => membersFor(e, level).source !== 'n/a').length }
}
